import { lazy } from "react";
import { createBrowserRouter, createRoutesFromElements, Route } from "react-router-dom";

import { SubscriptionProvider } from "../providers/SubscriptionProvider";

import SuspenseContainer from "../components/SuspenseContainer/SuspenseContainer.tsx";
import { AppLayout } from "../layout/AppLayout.tsx";
import { GiftsLayout } from "../layout/GiftsLayout.tsx";
import NotFound from "../views/NotFound/NotFound.tsx";

const HomeView = lazy(() => import("../views/Home/HomeView"));
const DashboardView = lazy(() => import("../views/Dashboard/DashboardView"));

// campaigns
const CampaignView = lazy(() => import("../views/Campaign/CampaignView"));
const DemoCampaignsView = lazy(() => import("../views/Campaign/DemoCampaignsView"));
const CampaignWizard = lazy(() => import("../views/CampaignWizard/CampaignWizard"));
const EditGiftView = lazy(() => import("../views/Campaign/CampaignGiftChoice/EditGiftView"));

// reviews
const ReviewsManagementView = lazy(
  () => import("../views/ReviewsManagement/ReviewsManagementView")
);
const ConfigView = lazy(() => import("../views/ReviewsManagement/components/wizard/ConfigView"));

// SMS campaigns
const ListView = lazy(() => import("../views/SMSCampaign/ListView"));
const DetailsView = lazy(() => import("../views/SMSCampaign/DetailsView"));
const CreateView = lazy(() => import("../views/SMSCampaign/CreateView"));

// misc
const UsersView = lazy(() => import("../views/Users/UsersView"));
const AccountView = lazy(() => import("../views/Account/AccountView"));

// auth
const Login = lazy(() => import("../views/Login/Login"));
const Register = lazy(() => import("../views/Register/Register"));
const Password = lazy(() => import("../views/Password/Password"));
const ResetPasswordRedirect = lazy(() => import("../views/Login/ResetPasswordRedirect"));
const Orders = lazy(() => import("../views/Orders/Orders"));
const Configs = lazy(() => import("../views/Orders/Configs"));
const Chat = lazy(() => import("../views/Orders/Chat"));
const AdminPlayersView = lazy(() => import("../views/Users/AdminPlayersView"));

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route element={<AppLayout />}>
        <Route index element={<HomeView />} />

        <Route path="google" element={<DashboardView type="google" />} />
        <Route path="instagram" element={<DashboardView type="socials" subType="INSTAGRAM" />} />
        <Route path="tiktok" element={<DashboardView type="socials" subType="TIKTOK" />} />
        <Route path="facebook" element={<DashboardView type="socials" subType="FACEBOOK" />} />
        <Route path="socials" element={<DashboardView type="socials" />} />
        <Route path="mail" element={<DashboardView type="mail" />} />
        <Route path="referral" element={<DashboardView type="referral" />} />

        <Route path="campaign" element={<CampaignView />} />
        <Route path="campaign-wizard" element={<CampaignWizard />} />
        <Route path="demo-campaigns" element={<DemoCampaignsView />} />

        <Route path="reviews-management" element={<ReviewsManagementView />} />
        <Route path="reviews-management/config" element={<ConfigView />} />
        <Route path="googleCb" element={<ReviewsManagementView />} />

        <Route path="sms-campaigns">
          <Route index element={<ListView />} />
          <Route path="new" element={<CreateView />} />
          <Route path=":id" element={<DetailsView />} />
        </Route>

        <Route path="users" element={<UsersView />} />

        <Route
          path="account"
          element={
            <SubscriptionProvider>
              <AccountView />
            </SubscriptionProvider>
          }
        />

        <Route path="orders">
          <Route index element={<Orders />} />
          <Route path="configs" element={<Configs />} />
          <Route path="chat" element={<Chat />} />
        </Route>
        <Route path="admin-players" element={<AdminPlayersView />} />
      </Route>

      <Route element={<GiftsLayout />}>
        <Route path="campaign/gifts/:id" element={<EditGiftView />} />
      </Route>

      {/* ---------- Public / auth pages ---------- */}
      <Route
        path="login"
        element={
          <SuspenseContainer>
            <Login />
          </SuspenseContainer>
        }
      />
      <Route
        path="confirm-email"
        element={
          <SuspenseContainer>
            <Login />
          </SuspenseContainer>
        }
      />
      <Route
        path="register"
        element={
          <SuspenseContainer>
            <Register />
          </SuspenseContainer>
        }
      />
      <Route
        path="confirm-password"
        element={
          <SuspenseContainer>
            <Password />
          </SuspenseContainer>
        }
      />
      <Route
        path="reset-password"
        element={
          <SuspenseContainer>
            <ResetPasswordRedirect />
          </SuspenseContainer>
        }
      />

      <Route
        path="*"
        element={
          <SuspenseContainer>
            <NotFound />
          </SuspenseContainer>
        }
      />

    </>
  )
);

export default router;
